import { PerspectiveCamera, WebGLRenderer } from "three";
import { property } from "lit/decorators.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { SSRPass } from "three/examples/jsm/postprocessing/SSRPass.js";
import { customElement } from "lit/decorators.js";
import { SugarElement } from "../../viewport/SugarCanvas";
import { Engine, Entity } from "../../../engine/Engine";
import { logger } from "../../../util/Logger";
import type {
  RenderSettings,
  SsgiOutputMode,
  SsgiSparseAdaptive,
  SsgiSparseEdgeKernel,
  SsgiSparseOutputMode,
  SsgiSparseVersion,
} from "../RenderSettingsStore";
import {
  SsgiTslEffect,
  type SsgiEffectOptions,
} from "./tsl/SsgiTslEffect";
import {
  SparseGiTslEffect,
  type SparseGiEffectOptions,
} from "./tsl/SparseGiTslEffect";
import {
  SparseGiV2TslEffect,
  type SparseGiV2Options,
} from "./tsl/SparseGiV2TslEffect";

/**
 * WebGPU: classic SsgiTslEffect or sparse GI (mutually exclusive).
 * Sparse GI: `sparseVersion` "v1" → SparseGiTslEffect, "v2" → SparseGiV2TslEffect.
 * WebGL: SSRPass bouncing approx (classic SSGI only).
 */
class SSGIPostProcessEntity extends Entity {
  private _ssgiPass?: SSRPass;
  private _tslEffect?: SsgiTslEffect;
  private _sparseEffect?: SparseGiTslEffect | SparseGiV2TslEffect;
  private _savedRenderPass?: RenderPass;
  private _settingsEnabled = false;
  private _sparseEnabled = false;

  output: SsgiOutputMode = "combined";
  sparseOutput: SsgiSparseOutputMode = "combined";
  sliceCount = 3;
  stepCount = 8;
  radius = 12;
  thickness = 1;
  expFactor = 2;
  aoIntensity = 1;
  giIntensity = 10;
  backfaceLighting = 0;
  useLinearThickness = false;
  useScreenSpaceSampling = true;
  useTemporalFiltering = true;
  sparseSliceCount = 3;
  sparseStepCount = 8;
  sparseRadius = 12;
  sparseThickness = 1;
  sparseExpFactor = 2;
  sparseGiIntensity = 10;
  sparseBackfaceLighting = 0;
  sparseUseLinearThickness = false;
  sparseUseScreenSpaceSampling = true;
  sparseUseTemporalFiltering = true;
  giStride = 2;
  giAdaptive: SsgiSparseAdaptive = "off";
  giEdgeThickness = 2;
  giEdgeKernel: SsgiSparseEdgeKernel = "sobel";
  giEdgeThreshold = 0.04;
  giDepthThreshold = 0.25;
  giNormalThreshold = 0.8;
  giMinOkCount = 3;
  sparseVersion: SsgiSparseVersion = "v1";
  sparseHistoryLength = 32;
  sparseDenoisePasses = 2;
  sparseEdgeRefine = true;
  sparseAoIntensity = 1;

  private syncSsrSize(): void {
    const eng = this.engine;
    if (!eng?.renderer.instance || !this._ssgiPass) return;
    const gl = eng.renderer.instance;
    if (!(gl instanceof WebGLRenderer)) return;
    const pr = Math.max(1, gl.getPixelRatio());
    const w = Math.max(1, Math.floor(eng.width * pr));
    const h = Math.max(1, Math.floor(eng.height * pr));
    this._ssgiPass.setSize(w, h);
  }

  private syncSsrFromCamera(): void {
    const eng = this.engine;
    if (!eng?.activeScene || !eng.activeCamera || !this._ssgiPass) return;
    const cam = eng.activeCamera.instance;
    this._ssgiPass.scene = eng.activeScene;
    this._ssgiPass.camera = cam;
    if (cam instanceof PerspectiveCamera) {
      this._ssgiPass.ssrMaterial.uniforms["cameraNear"].value = cam.near;
      this._ssgiPass.ssrMaterial.uniforms["cameraFar"].value = cam.far;
    }
    this._ssgiPass.ssrMaterial.uniforms["cameraProjectionMatrix"].value.copy(
      cam.projectionMatrix,
    );
    this._ssgiPass.ssrMaterial.uniforms[
      "cameraInverseProjectionMatrix"
    ].value.copy(cam.projectionMatrixInverse);
  }

  private readonly _updateCameraBound = () => {
    this.syncSsrFromCamera();
  };

  private readonly _engineResizeBound = () => {
    this.syncSsrSize();
  };

  private _params(): SsgiEffectOptions {
    return {
      output: this.output,
      sliceCount: this.sliceCount,
      stepCount: this.stepCount,
      radius: this.radius,
      thickness: this.thickness,
      expFactor: this.expFactor,
      aoIntensity: this.aoIntensity,
      giIntensity: this.giIntensity,
      backfaceLighting: this.backfaceLighting,
      useLinearThickness: this.useLinearThickness,
      useScreenSpaceSampling: this.useScreenSpaceSampling,
      useTemporalFiltering: this.useTemporalFiltering,
    };
  }

  private _sparseParams(): SparseGiEffectOptions {
    return {
      output: this.sparseOutput,
      sliceCount: this.sparseSliceCount,
      stepCount: this.sparseStepCount,
      radius: this.sparseRadius,
      thickness: this.sparseThickness,
      expFactor: this.sparseExpFactor,
      giIntensity: this.sparseGiIntensity,
      backfaceLighting: this.sparseBackfaceLighting,
      useLinearThickness: this.sparseUseLinearThickness,
      useScreenSpaceSampling: this.sparseUseScreenSpaceSampling,
      useTemporalFiltering: this.sparseUseTemporalFiltering,
      giStride: this.giStride,
      giAdaptive: this.giAdaptive,
      giEdgeThickness: this.giEdgeThickness,
      giEdgeKernel: this.giEdgeKernel,
      giEdgeThreshold: this.giEdgeThreshold,
      giDepthThreshold: this.giDepthThreshold,
      giNormalThreshold: this.giNormalThreshold,
      giMinOkCount: this.giMinOkCount,
    };
  }

  private _sparseV2Params(): Partial<SparseGiV2Options> {
    return {
      output: this.sparseOutput,
      sliceCount: this.sparseSliceCount,
      stepCount: this.sparseStepCount,
      radius: this.sparseRadius,
      thickness: this.sparseThickness,
      expFactor: this.sparseExpFactor,
      giIntensity: this.sparseGiIntensity,
      backfaceLighting: this.sparseBackfaceLighting,
      useLinearThickness: this.sparseUseLinearThickness,
      useScreenSpaceSampling: this.sparseUseScreenSpaceSampling,
      stride: this.giStride,
      historyLength: this.sparseHistoryLength,
      denoisePasses: this.sparseDenoisePasses,
      edgeRefine: this.sparseEdgeRefine,
      aoIntensity: this.sparseAoIntensity,
    };
  }

  /** Açık seyrek efekte güncel ayarları verir. @returns graf yeniden kurulmalı mı */
  private _updateSparseEffect(): boolean {
    const effect = this._sparseEffect;
    if (effect instanceof SparseGiV2TslEffect) {
      return effect.setParameters(this._sparseV2Params());
    }
    return effect?.setParameters(this._sparseParams()) ?? false;
  }

  private _createSparseEffect(): SparseGiTslEffect | SparseGiV2TslEffect {
    return this.sparseVersion === "v2"
      ? new SparseGiV2TslEffect(this._sparseV2Params())
      : new SparseGiTslEffect(this._sparseParams());
  }

  private _sparseEffectMatchesVersion(): boolean {
    const isV2 = this._sparseEffect instanceof SparseGiV2TslEffect;
    return isV2 === (this.sparseVersion === "v2");
  }

  applyParameters(partial: SsgiEffectOptions): void {
    let changed = false;
    const assign = <K extends keyof SsgiEffectOptions>(
      key: K,
      value: SsgiEffectOptions[K] | undefined,
    ) => {
      if (value == null || this[key as keyof this] === (value as never)) return;
      (this as Record<string, unknown>)[key as string] = value;
      changed = true;
    };
    assign("output", partial.output);
    assign("sliceCount", partial.sliceCount);
    assign("stepCount", partial.stepCount);
    assign("radius", partial.radius);
    assign("thickness", partial.thickness);
    assign("expFactor", partial.expFactor);
    assign("aoIntensity", partial.aoIntensity);
    assign("giIntensity", partial.giIntensity);
    assign("backfaceLighting", partial.backfaceLighting);
    assign("useLinearThickness", partial.useLinearThickness);
    assign("useScreenSpaceSampling", partial.useScreenSpaceSampling);
    assign("useTemporalFiltering", partial.useTemporalFiltering);

    if (!this._tslEffect && !changed) return;

    const graphDirty = this._tslEffect?.setParameters(this._params()) ?? false;
    if (graphDirty) this.engine?.invalidateTslEffects();
    if (changed || graphDirty) {
      this.engine?.dispatchCustomEvent("request-update");
    }
  }

  applySparseParameters(
    partial: SparseGiEffectOptions & {
      version?: SsgiSparseVersion;
      historyLength?: number;
      denoisePasses?: number;
      edgeRefine?: boolean;
      aoIntensity?: number;
    },
  ): void {
    let changed = false;
    const assign = (
      key: string,
      field: keyof SSGIPostProcessEntity,
      value: unknown,
    ) => {
      if (value == null || this[field] === (value as never)) return;
      (this as Record<string, unknown>)[field as string] = value;
      changed = true;
    };
    if (partial.output != null && this.sparseOutput !== partial.output) {
      this.sparseOutput = partial.output;
      changed = true;
    }
    assign("sliceCount", "sparseSliceCount", partial.sliceCount);
    assign("stepCount", "sparseStepCount", partial.stepCount);
    assign("radius", "sparseRadius", partial.radius);
    assign("thickness", "sparseThickness", partial.thickness);
    assign("expFactor", "sparseExpFactor", partial.expFactor);
    assign("giIntensity", "sparseGiIntensity", partial.giIntensity);
    assign("backfaceLighting", "sparseBackfaceLighting", partial.backfaceLighting);
    assign(
      "useLinearThickness",
      "sparseUseLinearThickness",
      partial.useLinearThickness,
    );
    assign(
      "useScreenSpaceSampling",
      "sparseUseScreenSpaceSampling",
      partial.useScreenSpaceSampling,
    );
    assign(
      "useTemporalFiltering",
      "sparseUseTemporalFiltering",
      partial.useTemporalFiltering,
    );
    assign("version", "sparseVersion", partial.version);
    assign("historyLength", "sparseHistoryLength", partial.historyLength);
    assign("denoisePasses", "sparseDenoisePasses", partial.denoisePasses);
    assign("edgeRefine", "sparseEdgeRefine", partial.edgeRefine);
    assign("aoIntensity", "sparseAoIntensity", partial.aoIntensity);
    if (partial.giStride != null && this.giStride !== partial.giStride) {
      this.giStride = partial.giStride;
      changed = true;
    }
    if (
      partial.giAdaptive != null &&
      this.giAdaptive !== partial.giAdaptive
    ) {
      this.giAdaptive = partial.giAdaptive;
      changed = true;
    }
    if (
      partial.giEdgeThickness != null &&
      this.giEdgeThickness !== partial.giEdgeThickness
    ) {
      this.giEdgeThickness = partial.giEdgeThickness;
      changed = true;
    }
    if (
      partial.giEdgeKernel != null &&
      this.giEdgeKernel !== partial.giEdgeKernel
    ) {
      this.giEdgeKernel = partial.giEdgeKernel;
      changed = true;
    }
    if (
      partial.giEdgeThreshold != null &&
      this.giEdgeThreshold !== partial.giEdgeThreshold
    ) {
      this.giEdgeThreshold = partial.giEdgeThreshold;
      changed = true;
    }
    if (
      partial.giDepthThreshold != null &&
      this.giDepthThreshold !== partial.giDepthThreshold
    ) {
      this.giDepthThreshold = partial.giDepthThreshold;
      changed = true;
    }
    if (
      partial.giNormalThreshold != null &&
      this.giNormalThreshold !== partial.giNormalThreshold
    ) {
      this.giNormalThreshold = partial.giNormalThreshold;
      changed = true;
    }
    if (
      partial.giMinOkCount != null &&
      this.giMinOkCount !== partial.giMinOkCount
    ) {
      this.giMinOkCount = partial.giMinOkCount;
      changed = true;
    }

    if (!this._sparseEffect && !changed) return;

    const graphDirty = this._updateSparseEffect();
    if (graphDirty) this.engine?.invalidateTslEffects();
    if (changed || graphDirty) {
      this.engine?.dispatchCustomEvent("request-update");
    }
  }

  setEnabled(enabled: boolean): void {
    this._settingsEnabled = enabled;
    if (enabled) this._sparseEnabled = false;
    this._syncEffects();
  }

  setSparseEnabled(enabled: boolean): void {
    this._sparseEnabled = enabled;
    if (enabled) this._settingsEnabled = false;
    this._syncEffects();
  }

  private _removeClassicTsl(eng: Engine): void {
    if (!this._tslEffect) return;
    this._tslEffect.dispose();
    eng.removeEffect(this._tslEffect);
    this._tslEffect = undefined;
  }

  private _removeSparseTsl(eng: Engine): void {
    if (!this._sparseEffect) return;
    this._sparseEffect.dispose();
    eng.removeEffect(this._sparseEffect);
    this._sparseEffect = undefined;
  }

  private _syncEffects(): void {
    const eng = this.engine;
    if (!eng) return;

    if (eng.renderer.isWebGPURenderer) {
      if (this._sparseEnabled) {
        this._removeClassicTsl(eng);
        if (this._sparseEffect && !this._sparseEffectMatchesVersion()) {
          this._removeSparseTsl(eng);
        }
        if (!this._sparseEffect) {
          this._sparseEffect = this._createSparseEffect();
          eng.addEffect(this._sparseEffect);
        }
      } else if (this._settingsEnabled) {
        this._removeSparseTsl(eng);
        if (!this._tslEffect) {
          this._tslEffect = new SsgiTslEffect(this._params());
          eng.addEffect(this._tslEffect);
        }
      } else {
        this._removeClassicTsl(eng);
        this._removeSparseTsl(eng);
      }
      eng.dispatchCustomEvent("request-update");
      return;
    }

    if (!this._settingsEnabled) {
      if (this._ssgiPass) {
        this._ssgiPass.enabled = false;
        eng.dispatchCustomEvent("request-update");
      }
      return;
    }

    if (this._ssgiPass) {
      this._ssgiPass.enabled = true;
      eng.dispatchCustomEvent("request-update");
    } else {
      this.addSSGIPass();
    }
  }

  private addSSGIPass(): void {
    const eng = this.engine;
    if (!eng?.activeScene || !eng.activeCamera) return;
    // Sparse GI too: a saved / default setting arrives before the engine.
    if (!this._settingsEnabled && !this._sparseEnabled) return;

    if (eng.renderer.isWebGPURenderer) {
      this._syncEffects();
      return;
    }

    // Sparse GI is WebGPU only; below is the classic WebGL pass.
    if (!this._settingsEnabled) return;

    if (this._ssgiPass) {
      this._ssgiPass.enabled = true;
      return;
    }

    if (!(eng.renderer.instance instanceof WebGLRenderer)) return;

    const gl = eng.renderer.instance;
    const pr = Math.max(1, gl.getPixelRatio());
    const w = Math.max(1, Math.floor(eng.width * pr));
    const h = Math.max(1, Math.floor(eng.height * pr));

    this._savedRenderPass = undefined;
    this._ssgiPass = new SSRPass({
      renderer: gl,
      scene: eng.activeScene,
      camera: eng.activeCamera.instance,
      width: w,
      height: h,
      selects: null,
      groundReflector: null,
      isBouncing: true,
    });
    this._ssgiPass.opacity = 1.25;

    eng.mutatePassComposer((composer) => {
      const rp = composer.passes.find((p) => p instanceof RenderPass) as
        | RenderPass
        | undefined;
      if (!rp) {
        logger.warn(
          "[SSGIPostProcess] RenderPass yok; composer henüz kurulmamış olabilir.",
        );
        return;
      }
      composer.removePass(rp);
      this._savedRenderPass = rp;
      composer.insertPass(this._ssgiPass!, 0);
    });

    if (!this._savedRenderPass) {
      this._ssgiPass.dispose();
      this._ssgiPass = undefined;
      return;
    }

    eng.addEventListener("update-camera", this._updateCameraBound);
    eng.addEventListener("engine-resize", this._engineResizeBound);
  }

  onAdd(_engine: Engine): void {
    // Same as HEAD: attach when scene/camera ready. Do not gate on store here —
    // a stuck pathTrace localStorage would leave SSGI permanently off.
    const tryAdd = () => {
      if (!this.engine?.activeCamera || !this.engine.activeScene) {
        requestAnimationFrame(tryAdd);
        return;
      }
      this.addSSGIPass();
    };
    tryAdd();
  }

  onRemove(engine: Engine): void {
    if (this._tslEffect) {
      this._tslEffect.dispose();
      engine.removeEffect(this._tslEffect);
      this._tslEffect = undefined;
    }
    if (this._sparseEffect) {
      this._sparseEffect.dispose();
      engine.removeEffect(this._sparseEffect);
      this._sparseEffect = undefined;
    }
    if (this._ssgiPass) {
      engine.mutatePassComposerIfExists((composer) => {
        composer.removePass(this._ssgiPass!);
        if (
          this._savedRenderPass &&
          !composer.passes.includes(this._savedRenderPass)
        ) {
          composer.insertPass(this._savedRenderPass, 0);
        }
      });
      this._ssgiPass.dispose();
      this._ssgiPass = undefined;
    }
    this._savedRenderPass = undefined;
    engine.removeEventListener("update-camera", this._updateCameraBound);
    engine.removeEventListener("engine-resize", this._engineResizeBound);
  }
}

@customElement("ssgi-postprocess")
export class SSGIPostProcessElement extends SugarElement {
  @property({ type: Number, attribute: "slice-count" }) sliceCount = 3;
  @property({ type: Number, attribute: "step-count" }) stepCount = 8;
  @property({ type: Number, attribute: "ssgi-radius" }) ssgiRadius = 12;
  @property({ type: Number, attribute: "ssgi-thickness" }) ssgiThickness = 1;
  @property({ type: Number, attribute: "exp-factor" }) expFactor = 2;
  @property({ type: Number, attribute: "ao-intensity" }) aoIntensity = 1;
  @property({ type: Number, attribute: "gi-intensity" }) giIntensity = 10;

  createEntity(): Entity | undefined {
    const entity = new SSGIPostProcessEntity();
    entity.sliceCount = this.sliceCount;
    entity.stepCount = this.stepCount;
    entity.radius = this.ssgiRadius;
    entity.thickness = this.ssgiThickness;
    entity.expFactor = this.expFactor;
    entity.aoIntensity = this.aoIntensity;
    entity.giIntensity = this.giIntensity;
    return entity;
  }

  updated(changed: Map<string, unknown>): void {
    super.updated?.(changed as never);
    const ent = this.entity as SSGIPostProcessEntity | undefined;
    if (!ent) return;
    if (
      changed.has("sliceCount") ||
      changed.has("stepCount") ||
      changed.has("ssgiRadius") ||
      changed.has("ssgiThickness") ||
      changed.has("expFactor") ||
      changed.has("aoIntensity") ||
      changed.has("giIntensity")
    ) {
      ent.applyParameters({
        sliceCount: this.sliceCount,
        stepCount: this.stepCount,
        radius: this.ssgiRadius,
        thickness: this.ssgiThickness,
        expFactor: this.expFactor,
        aoIntensity: this.aoIntensity,
        giIntensity: this.giIntensity,
      });
    }
  }

  applySettings(settings: RenderSettings["ssgi"]): void {
    this.sliceCount = settings.sliceCount;
    this.stepCount = settings.stepCount;
    this.ssgiRadius = settings.radius;
    this.ssgiThickness = settings.thickness;
    this.expFactor = settings.expFactor;
    this.aoIntensity = settings.aoIntensity;
    this.giIntensity = settings.giIntensity;
    const ent = this.entity as SSGIPostProcessEntity | undefined;
    if (!ent) return;
    ent.applyParameters({
      output: settings.output,
      sliceCount: settings.sliceCount,
      stepCount: settings.stepCount,
      radius: settings.radius,
      thickness: settings.thickness,
      expFactor: settings.expFactor,
      aoIntensity: settings.aoIntensity,
      giIntensity: settings.giIntensity,
      backfaceLighting: settings.backfaceLighting,
      useLinearThickness: settings.useLinearThickness,
      useScreenSpaceSampling: settings.useScreenSpaceSampling,
      useTemporalFiltering: settings.useTemporalFiltering,
    });
    if (settings.enabled) {
      ent.setSparseEnabled(false);
      ent.setEnabled(true);
    } else {
      ent.setEnabled(false);
    }
  }

  applySparseSettings(settings: RenderSettings["ssgiSparse"]): void {
    const ent = this.entity as SSGIPostProcessEntity | undefined;
    if (!ent) return;
    ent.applySparseParameters({
      output: settings.output,
      sliceCount: settings.sliceCount,
      stepCount: settings.stepCount,
      radius: settings.radius,
      thickness: settings.thickness,
      expFactor: settings.expFactor,
      giIntensity: settings.giIntensity,
      backfaceLighting: settings.backfaceLighting,
      useLinearThickness: settings.useLinearThickness,
      useScreenSpaceSampling: settings.useScreenSpaceSampling,
      useTemporalFiltering: settings.useTemporalFiltering,
      giStride: settings.giStride,
      giAdaptive: settings.giAdaptive,
      giEdgeThickness: settings.giEdgeThickness,
      giEdgeKernel: settings.giEdgeKernel,
      giEdgeThreshold: settings.giEdgeThreshold,
      giDepthThreshold: settings.giDepthThreshold,
      giNormalThreshold: settings.giNormalThreshold,
      giMinOkCount: settings.giMinOkCount,
      version: settings.version,
      historyLength: settings.historyLength,
      denoisePasses: settings.denoisePasses,
      edgeRefine: settings.edgeRefine,
      aoIntensity: settings.aoIntensity,
    });
    if (settings.enabled) {
      ent.setEnabled(false);
      ent.setSparseEnabled(true);
    } else {
      ent.setSparseEnabled(false);
    }
  }
}
